import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPublicSurveyInstance,
  createAnonymousSession,
  getSession,
  submitAnswer,
  submitBatchAnswers,
  completeSurveyResponse,
  getSurveyResponseProgress,
  validateSurveyResponse,
  getSurveyResponse,
  updateResponseMetadata,
  checkSurveyInstanceStatus,
  pingSession,
} from '../api/surveyResponseApi';
import { CreateSurveyResponseDTO, SubmitAnswerDTO, BatchSubmitAnswersDTO } from '../dtos';
import { surveyResponseQueryKeys } from './queryKeys';

/**
 * Hook to get public survey instance data (no auth required)
 */
export const usePublicSurveyInstance = (instanceId: string, surveyId: string, enabled = true) => {
  return useQuery({
    queryKey: [...surveyResponseQueryKeys.publicInstance(instanceId), surveyId],
    queryFn: () => getPublicSurveyInstance(instanceId, surveyId),
    enabled: !!instanceId && !!surveyId && enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to check survey instance status
 */
export const useSurveyInstanceStatus = (instanceId: string, enabled = true) => {
  return useQuery({
    queryKey: surveyResponseQueryKeys.instanceStatus(instanceId),
    queryFn: () => checkSurveyInstanceStatus(instanceId),
    enabled: !!instanceId && enabled,
    staleTime: 1000 * 60, // 1 minute
    refetchInterval: 1000 * 60 * 5, // Refetch every 5 minutes to check expiry
  });
};

/**
 * Hook to create anonymous session
 */
export const useCreateAnonymousSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      instanceId,
      sessionData,
      surveyId,
    }: {
      instanceId: string;
      sessionData: CreateSurveyResponseDTO;
      surveyId: string;
    }) => createAnonymousSession(instanceId, sessionData, surveyId),
    onSuccess: (data, variables) => {
      // Cache the session data
      queryClient.setQueryData(
        surveyResponseQueryKeys.session(variables.instanceId, data.sessionId),
        data
      );
    },
  });
};

/**
 * Hook to get existing session
 */
export const useSession = (
  instanceId: string,
  sessionId: string,
  surveyId: string,
  enabled = true
) => {
  return useQuery({
    queryKey: surveyResponseQueryKeys.session(instanceId, sessionId),
    queryFn: () => getSession(instanceId, sessionId, surveyId),
    enabled: !!instanceId && !!sessionId && !!surveyId && enabled,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

/**
 * Hook to submit answer for a single question
 */
export const useSubmitAnswer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      instanceId,
      sessionId,
      answerData,
      surveyId,
    }: {
      instanceId: string;
      sessionId: string;
      answerData: SubmitAnswerDTO;
      surveyId: string;
    }) => submitAnswer(instanceId, sessionId, answerData, surveyId),
    onSuccess: (data, variables) => {
      // Update progress cache
      queryClient.setQueryData(
        surveyResponseQueryKeys.sessionProgress(variables.instanceId, variables.sessionId),
        data
      );

      // Invalidate response data to refetch with new answer
      queryClient.invalidateQueries({
        queryKey: surveyResponseQueryKeys.response(variables.instanceId, variables.sessionId),
      });
    },
  });
};

/**
 * Hook to submit multiple answers at once
 */
export const useSubmitBatchAnswers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      instanceId,
      sessionId,
      batchData,
      surveyId,
    }: {
      instanceId: string;
      sessionId: string;
      batchData: BatchSubmitAnswersDTO;
      surveyId: string;
    }) => submitBatchAnswers(instanceId, sessionId, batchData, surveyId),
    onSuccess: (data, variables) => {
      // Update progress cache
      queryClient.setQueryData(
        surveyResponseQueryKeys.sessionProgress(variables.instanceId, variables.sessionId),
        data
      );

      // Invalidate response data
      queryClient.invalidateQueries({
        queryKey: surveyResponseQueryKeys.response(variables.instanceId, variables.sessionId),
      });
    },
  });
};

/**
 * Hook to complete survey response
 */
export const useCompleteSurveyResponse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      instanceId,
      sessionId,
      surveyId,
    }: {
      instanceId: string;
      sessionId: string;
      surveyId: string;
    }) => completeSurveyResponse(instanceId, sessionId, surveyId),
    onSuccess: (data, variables) => {
      // Update all related queries
      queryClient.invalidateQueries({
        queryKey: surveyResponseQueryKeys.session(variables.instanceId, variables.sessionId),
      });
      queryClient.invalidateQueries({
        queryKey: surveyResponseQueryKeys.response(variables.instanceId, variables.sessionId),
      });
      queryClient.invalidateQueries({
        queryKey: surveyResponseQueryKeys.sessionProgress(
          variables.instanceId,
          variables.sessionId
        ),
      });
    },
  });
};

/**
 * Hook to get survey response progress
 */
export const useSurveyResponseProgress = (
  instanceId: string,
  sessionId: string,
  surveyId: string,
  enabled = true
) => {
  return useQuery({
    queryKey: surveyResponseQueryKeys.sessionProgress(instanceId, sessionId),
    queryFn: () => getSurveyResponseProgress(instanceId, sessionId, surveyId),
    enabled: !!instanceId && !!sessionId && !!surveyId && enabled,
    staleTime: 1000 * 30, // 30 seconds
  });
};

/**
 * Hook to validate survey response
 */
export const useValidateSurveyResponse = () => {
  return useMutation({
    mutationFn: ({
      instanceId,
      sessionId,
      surveyId,
    }: {
      instanceId: string;
      sessionId: string;
      surveyId: string;
    }) => validateSurveyResponse(instanceId, sessionId, surveyId),
  });
};

/**
 * Hook to get current survey response data
 */
export const useSurveyResponse = (
  instanceId: string,
  sessionId: string,
  surveyId: string,
  enabled = true
) => {
  return useQuery({
    queryKey: surveyResponseQueryKeys.response(instanceId, sessionId),
    queryFn: () => getSurveyResponse(instanceId, sessionId, surveyId),
    enabled: !!instanceId && !!sessionId && !!surveyId && enabled,
    staleTime: 1000 * 60, // 1 minute
  });
};

/**
 * Hook to update response metadata
 */
export const useUpdateResponseMetadata = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      instanceId,
      sessionId,
      metadata,
    }: {
      instanceId: string;
      sessionId: string;
      metadata: { language?: string; userAgent?: string };
    }) => updateResponseMetadata(instanceId, sessionId, metadata),
    onSuccess: (data, variables) => {
      // Update response cache
      queryClient.setQueryData(
        surveyResponseQueryKeys.response(variables.instanceId, variables.sessionId),
        data
      );
    },
  });
};

/**
 * Hook to ping session to keep it alive
 */
export const usePingSession = () => {
  return useMutation({
    mutationFn: ({ instanceId, sessionId }: { instanceId: string; sessionId: string }) =>
      pingSession(instanceId, sessionId),
  });
};
